import {ChangeEvent} from 'react';
import {RoleMetadataType} from './ContentItemViewMetadataGeneral';

export type SetQcMetadataType = (id: number) => void;
export type TabLabelGetter = (value?: string) => string;
export type SetCurrentTab = (value: string) => void;
export type HandleChangeEvent = ChangeEvent<{}>;

export type Role = {
    label: string;
    id: number;
};

export const contentTypeRequiredFieldsConfig = {
    web_originals: [
        'title',
        'short-description',
        'long-description',
        'source-region',
        'release-date',
        'keywords',
        'genres',
        'sub-genres',
        'categories',
        'sub-categories',
    ],
    series: [
        'content-type',
        'season',
        'title',
        'short-description',
        'long-description',
        'genres',
        'sub-genres',
        'categories',
        'sub-categories',
    ],
    episode: [
        'allotment',
        'season',
        'episode',
        'title',
        'short-description',
        'long-description',
        'genres',
        'sub-genres',
        'source-region',
        'categories',
        'sub-categories',
        'release-date',
        'keywords',
    ],
    film: [
        'allotment',
        'season',
        'episode',
        'title',
        'short-description',
        'long-description',
        'genres',
        'sub-genres',
        'source-region',
        'categories',
        'sub-categories',
        'release-date',
        'keywords',
    ],
};

export enum MetadataContentType {
    WebOriginals = 'web_originals',
    Episode = 'episode',
    Film = 'film',
    Series = 'series',
}

export interface SubCategory {
    label: string;
}

export interface ContentItemGenresItem {
    contentItemId: number;
    createdAt: string;
    deletedAt: string | null;
    id: number;
    label: string;
    updatedAt: string;
}

export interface CategoryItem {
    createdAt?: string;
    deletedAt?: string | null;
    id?: number;
    label: string;
    languageMetumId?: number;
    updatedAt?: string;
    subCategories?: SubCategory[];
}

export type KeywordItem = CategoryItem;

export interface MetadataLanguageRoles {
    actors: Role[];
    directors: Role[];
    producers: Role[];
    composers: Role[];
    writers: Role[];
    roleMeta: RoleMetadataType[];
}

export interface MetadataLocalizedLanguage {
    title: string;
    longDescription: string;
    shortDescription: string;
    categories: CategoryItem[];
    subCategories: CategoryItem[];
    keywords: KeywordItem[];
    editable: boolean;
    id: number;
}

export interface MetadataTerritorialLanguage {
    rating?: string;
    captionRequired?: boolean;
    ratingDescriptors?: string[];
}

export interface MetadataGeneral {
    title?: string;
    contentId?: number;
    partner?: string;
    partnerHash?: string;
    availsId?: string;
    contentType?: string;
    sourceRegion?: string;
    episodeNumber?: number;
    seasonNumber?: number;
    releaseYear?: number;
    copyrightHolder?: string;
    copyrightYear?: number;
    releaseDate: string;
    defaultGenre: ContentItemGenresItem[];
    defaultSubGenre: ContentItemGenresItem[];
    roles: MetadataLanguageRoles;
    seriesTitle?: string;
    displayTitle?: string;
    mamId: string;
}

export interface MetadataTerritorial {
    [key: string]: MetadataTerritorialLanguage;
}

export interface MetadataLocalized {
    en?: MetadataLocalizedLanguage;
    es?: MetadataLocalizedLanguage;
    fr?: MetadataLocalizedLanguage;
}

export interface ContentItemViewMetadataState {
    general: MetadataGeneral;
    localized: MetadataLocalized;
    territorial: MetadataTerritorial;
    allotment: number | null;
    id: number;
    isLoading: boolean;
}

export interface ContentPartner {
    cmsPartnerId: string;
    createdAt: string;
    deletedAt: string | null;
    id: number;
    parentId: number | null;
    partnerName: string;
    updatedAt: string;
}

export interface LanguageMetaItem {
    categories: CategoryItem[];
    contentItemId: number;
    createdAt: string;
    deletedAt: string | null;
    displayTitle: string;
    id: number;
    keywords: KeywordItem[];
    language: string;
    title: string;
    updatedAt: string;
}

export interface RatingsItem {
    contentItemId: number;
    countryCode: string;
    createdAt: string;
    deletedAt: string | null;
    id: number;
    rating: string;
    ratingReason: string;
    updatedAt: string;
}

export interface RoleMetaItem {
    contentItemId: number;
    createdAt: string;
    deletedAt: string | null;
    id: number;
    label: string;
    roleMetaType: string;
    updatedAt: string;
}

export interface TerritorialMetaItem {
    captionsRequired: boolean;
    contentItemId: number;
    countryCode: string;
    id: number;
    rating: string;
    ratingDescriptors: string[];
}

export interface ContentItem {
    id: number;
    contentItemGenres: ContentItemGenresItem[];
    contentItemSubGenres: ContentItemGenresItem[];
    contentItemType: string;
    contentPartner: ContentPartner;
    contentPartnerId: number;
    copyrightHolder: string;
    copyrightYear: number;
    createdAt: string;
    deletedAt: string | null;
    languageMeta: LanguageMetaItem[];
    plutotvAvailsId: string;
    territorialMeta: TerritorialMetaItem[];
    ratings: RatingsItem[];
    releaseDate: string;
    roleMeta: RoleMetaItem[];
    sourceRegion?: string;
    number?: number;
    seasonNumber?: number;
    seriesTitle?: string;
    displayTitle?: string;
    mamId: string;
}

export interface Label {
    createdAt?: string;
    deletedAt?: string | null;
    id?: number;
    label: string;
    languageMetumId?: number;
    updatedAt?: string;
}

export interface Descriptor {
    label: string;
    code: string;
    countryCode: string;
}

export interface DescriptorsPayload {
    isLoading: boolean;
    descriptors: Descriptor[];
}

export interface FormFieldType {
    input: {
        onChange: (val: unknown) => void;
        value: unknown;
    };
}

export interface VideoTypeData {
    dropFrame: boolean;
    duration: number;
    frameRate: {
        denominator: number;
        numerator: number;
    };
}

export interface TerritoriaFormInitialValues {
    rating: string;
    captionRequired: string;
    ratingDescriptors: string[];
}
